import { computed, unref, toValue } from 'vue';
import { DateTime } from 'luxon';
import upperFirst from 'lodash/upperFirst';
import { YardTaskStatus, YardTaskType } from '@nova/core';

import {
  formatPersonName,
  formatRelativeTime,
  formatTaskCategoryName,
  formatTaskDate,
  formatTaskNumber,
  getTaskStatusIcon,
  getTaskStatusIconColor,
  getTaskStatusLabel,
  isCheckTask,
  isMoveTask,
  isTaskCreated,
  isTaskInProgress,
  isTaskCompleted,
  isTaskCancelled,
  isTaskStatusInProgress,
  isTaskStatusCompleted,
  isTaskStatusCancelled,
  isTaskIssue,
  isTaskUrgent,
  mapTags
} from '../helpers/yardTaskUtils';

import type { Ref, ComputedRef } from 'vue';
import type { Maybe, Nullable, TaskViewData, YardTaskCheckCategory } from '@nova/core';

export interface YardTaskPresenterDependencies {
  warehouseTimezone?:
    | string
    | Ref<Maybe<string>>
    | ComputedRef<Maybe<string>>
    | (() => Maybe<string>);
  i18n?: (key: string) => string;
  formatDate?: (
    date: Nullable<string>,
    format?: string,
    options?: { tz?: string }
  ) => Nullable<string>;
}

/**
 * Presenter for a yard task.
 *
 * TODO: (welington) translation keys are now hardcoded to Helios' i18n keys.
 * We should revisit that when we need to translate Circe.
 * @param task - The task to present
 * @param dependencies - The dependencies to use
 * @returns The presenter
 */

export function useYardTaskPresenter(
  task:
    | TaskViewData
    | Ref<Maybe<TaskViewData>>
    | ComputedRef<Maybe<TaskViewData>>
    | null
    | undefined,
  dependencies?: YardTaskPresenterDependencies
) {
  const {
    warehouseTimezone: providedTimezone,
    i18n,
    formatDate: providedFormatDate
  } = dependencies || {};

  const warehouseTimezone = computed(() => {
    return toValue(providedTimezone) || 'utc';
  });

  const currentTask = computed(() => {
    return unref(task) || null;
  });

  const formatDate = (
    date: Nullable<string>,
    format: string = 'MMM d, yyyy @ h:mm a'
  ): Nullable<string> => {
    if (!date) {
      return null;
    }

    if (providedFormatDate) {
      return providedFormatDate(date, format, { tz: warehouseTimezone.value });
    }

    return formatTaskDate(date, format, warehouseTimezone.value);
  };

  const getStatusIcon = (status?: Maybe<string>): string => {
    return getTaskStatusIcon(status as YardTaskStatus);
  };

  const getStatusLabel = (status?: Maybe<string>): string => {
    if (!status) {
      return '--';
    }

    if (i18n) {
      if (isTaskStatusInProgress(status)) {
        return i18n('yard.pages.yardTasks.labels.inProgress');
      }
      if (isTaskStatusCompleted(status)) {
        return i18n('yard.pages.yardTasks.labels.completed');
      }
      if (isTaskStatusCancelled(status)) {
        return i18n('yard.pages.yardTasks.labels.canceled');
      }
      return i18n('yard.pages.yardTasks.labels.toDo');
    }

    return getTaskStatusLabel(status as YardTaskStatus);
  };

  const getStatusIconColor = (status?: Maybe<string>): Nullable<string> => {
    return getTaskStatusIconColor(status as YardTaskStatus);
  };

  const taskId = computed(() => {
    return currentTask.value?.id;
  });

  const taskNumber = computed(() => {
    return formatTaskNumber(currentTask.value?.refNum);
  });

  const taskDescription = computed(() => {
    return currentTask.value?.description || null;
  });

  const taskStatus = computed(() => {
    return currentTask.value?.status || null;
  });

  const taskTags = computed(() => {
    return mapTags(currentTask.value?.tags);
  });

  const isUrgent = computed(() => {
    return isTaskUrgent(currentTask.value);
  });

  const hasIssue = computed(() => {
    return isTaskIssue(currentTask.value);
  });

  const taskType = computed(() => currentTask.value?.type || null);

  const formattedAssigneeName = computed(() => {
    const assignedTo = currentTask.value?.assignedTo;

    if (!assignedTo) {
      if (i18n) {
        return i18n('yard.pages.yardTasks.messages.unassigned');
      }
      return 'Unassigned';
    }

    return formatPersonName(assignedTo) || 'Unassigned';
  });

  // the dueDateTime from API (ISO string)
  const dueDateTime = computed(() => {
    return currentTask.value?.dueDateTime || null;
  });

  const formattedDueDate = computed(() => {
    const formatted = formatDate(dueDateTime.value);

    if (!formatted) {
      if (i18n) {
        return i18n('yard.pages.yardTasks.messages.noDueDate');
      }

      return 'No due date';
    }

    return formatted;
  });

  const isOverdue = computed(() => {
    if (
      !dueDateTime.value ||
      isTaskStatusCompleted(taskStatus.value) ||
      isTaskStatusCancelled(taskStatus.value)
    ) {
      return false;
    }

    const dueDateInTz = DateTime.fromISO(dueDateTime.value).setZone(warehouseTimezone.value);
    const nowInTz = DateTime.now().setZone(warehouseTimezone.value);

    return dueDateInTz < nowInTz;
  });

  const createdAt = computed(() => {
    const formatted = formatDate(currentTask.value?.createdAt || null);
    return formatted || '--';
  });

  // Status computed properties
  const statusIcon = computed(() => {
    return getStatusIcon(taskStatus.value);
  });

  const statusLabel = computed(() => {
    return getStatusLabel(taskStatus.value);
  });

  // Check task specific getters
  const getTaskCategory = () => {
    if (!isCheckTask(currentTask.value)) {
      return null;
    }

    return currentTask.value?.category || null;
  };

  const getRelatedEntity = () => {
    if (!isCheckTask(currentTask.value)) {
      return null;
    }

    return currentTask.value?.relatedEntity ?? null;
  };

  const getRelatedId = () => {
    if (!isCheckTask(currentTask.value)) {
      return null;
    }

    return currentTask.value?.relatedId || null;
  };

  const getRelatedObject = () => {
    if (!isCheckTask(currentTask.value)) {
      return null;
    }

    return currentTask.value?.relatedObject || null;
  };

  const getTaskChecklist = () => {
    if (!isCheckTask(currentTask.value)) {
      return null;
    }

    return currentTask.value?.checklist || null;
  };

  // Move task specific getters
  const getAssetContainer = () => {
    if (!isMoveTask(currentTask.value)) {
      return null;
    }

    return currentTask.value?.assetContainer || null;
  };

  const getAssetContainerCode = () => {
    if (!isMoveTask(currentTask.value)) {
      return null;
    }

    const assetContainer = getAssetContainer();

    if (!assetContainer) {
      return 'Any asset';
    }

    return assetContainer.code || 'N/A';
  };

  const getAssetContainerType = () => {
    if (!isMoveTask(currentTask.value)) {
      return null;
    }

    const assetContainer = getAssetContainer();

    if (!assetContainer) {
      return null;
    }

    return assetContainer.type || null;
  };

  const getOriginSpot = () => {
    if (!isMoveTask(currentTask.value)) {
      return null;
    }

    return currentTask.value?.originSpot || null;
  };

  const getDestinationSpot = () => {
    if (!isMoveTask(currentTask.value)) {
      return null;
    }

    return currentTask.value?.destinationSpot || null;
  };

  const getOriginSpotId = () => {
    return getOriginSpot()?.id || null;
  };

  const getDestinationSpotId = () => {
    return getDestinationSpot()?.id || null;
  };

  const getTaskTypeLabel = () => {
    if (!taskType.value) {
      return 'No type';
    }

    if (isCheckTask(currentTask.value)) {
      return formatTaskCategoryName(
        getTaskCategory() as Maybe<YardTaskCheckCategory>,
        currentTask.value?.type as Maybe<YardTaskType>
      );
    }

    return upperFirst(taskType.value);
  };

  const getEvents = () => {
    return currentTask.value?.events || [];
  };

  const getTransitionEventByEvent = (eventType: string) => {
    const events = getEvents();
    const event = events.find(e => e.event === eventType) || null;

    if (!event) {
      return null;
    }

    return {
      at: formatDate(event.createDateTime) || null,
      rawAt: event.createDateTime || null,
      by: formatPersonName(event.data?.user) || '--',
      rawBy: event.data?.user || null
    };
  };

  const inProgressEvent = computed(() => {
    return getTransitionEventByEvent('in-progress');
  });

  const completedEvent = computed(() => {
    return getTransitionEventByEvent('completed');
  });

  const cancelledEvent = computed(() => {
    return getTransitionEventByEvent('cancelled');
  });

  const createdBy = computed(() => {
    const createdByUser = currentTask.value?.createdBy;

    if (createdByUser) {
      return formatPersonName(createdByUser);
    }

    return null;
  });

  return {
    warehouseTimezone,

    // Basic task info
    taskId,
    taskNumber,
    taskDescription,
    taskStatus,
    taskType,
    taskTags,

    // Flags
    isUrgent,
    hasIssue,

    // Task type
    isMoveTask: computed(() => isMoveTask(currentTask.value)),
    isCheckTask: computed(() => isCheckTask(currentTask.value)),

    // Assigned user
    formattedAssigneeName,

    // Dates
    formattedDueDate,
    dueDateTime,
    isOverdue,
    createdAt,

    // Status
    statusIcon,
    statusLabel,
    statusIconColor: computed(() => getStatusIconColor(taskStatus.value)),
    isTaskCreated: computed(() => isTaskCreated(currentTask.value)),
    isTaskInProgress: computed(() => isTaskInProgress(currentTask.value)),
    isTaskCompleted: computed(() => isTaskCompleted(currentTask.value)),
    isTaskCancelled: computed(() => isTaskCancelled(currentTask.value)),

    // Check task specific getters
    getTaskCategory,
    getRelatedEntity,
    getRelatedId,
    getRelatedObject,
    getTaskChecklist,
    getTaskTypeLabel,

    // Move task specific getters
    getAssetContainer,
    getAssetContainerCode,
    getAssetContainerType,
    getOriginSpot,
    getDestinationSpot,
    getOriginSpotId,
    getDestinationSpotId,

    // Event computed properties (reactive)
    inProgressEvent,
    completedEvent,
    cancelledEvent,
    createdBy,

    // Formatting functions
    formatPersonName,
    formatDate,
    formatTaskNumber,
    formatRelativeTime: (dateTime: string) => formatRelativeTime(dateTime, warehouseTimezone.value),
    getStatusIcon,
    getStatusLabel,
    getStatusIconColor
  };
}
